// @prod-safe — passive DOM scanner, renders null and only logs to console.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Runtime safety net that scans the rendered DOM after every route change
 * and reports any leaked dev/debug/overlay UI. Active in both dev and prod
 * so a regression never silently ships.
 *
 * What it flags:
 *  - Text nodes matching the dev hero-overlay toggle ("Overlay: off|soft|medium|strong")
 *  - Elements carrying known debug-tool data attributes / ids / class fragments
 *
 * What it does:
 *  - DEV builds: console.error + visual outline so it's impossible to miss
 *  - PROD builds: single console.warn (no UI noise, no crash)
 *
 * It is intentionally narrow — it matches on highly-specific patterns rather
 * than the bare word "Overlay" so it never false-positives on real copy.
 */

// Highly-specific text patterns that should never appear in production UI.
const FORBIDDEN_TEXT_PATTERNS: RegExp[] = [
  /^\s*Overlay:\s*(off|soft|medium|strong)\s*$/i,
  /^\s*Hero overlay\s*$/i,
  /^\s*Debug toolbar\s*$/i,
];

// Attribute / id / class selectors associated with known debug UIs.
const FORBIDDEN_SELECTORS: string[] = [
  "[data-dev-toolbar]",
  "[data-debug-overlay]",
  "[data-testing-helper]",
  "[data-accessibility-overlay]",
  "#dev-toolbar",
  "#debug-overlay",
  "#hero-overlay-dev-toggle",
  ".dev-toolbar",
  ".debug-overlay",
  ".accessibility-overlay",
];

interface Hit {
  reason: string;
  snippet: string;
  element: Element;
}

const scanDom = (root: ParentNode = document.body): Hit[] => {
  const hits: Hit[] = [];

  // 1. Selector-based debug containers.
  for (const sel of FORBIDDEN_SELECTORS) {
    root.querySelectorAll(sel).forEach((el) => {
      hits.push({
        reason: `Matched debug selector: ${sel}`,
        snippet: el.outerHTML.slice(0, 160),
        element: el,
      });
    });
  }

  // 2. Text-pattern walk — leaf elements only to keep it cheap.
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const text = node.nodeValue ?? "";
      if (text.length === 0 || text.length > 80) return NodeFilter.FILTER_REJECT;
      return FORBIDDEN_TEXT_PATTERNS.some((re) => re.test(text))
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  let current = walker.nextNode();
  while (current) {
    const el = (current.parentElement ?? document.body) as Element;
    // Ignore matches inside <script>, <style>, <template>, <noscript>.
    const tag = el.tagName.toLowerCase();
    if (!["script", "style", "template", "noscript"].includes(tag)) {
      hits.push({
        reason: `Matched forbidden text: "${(current.nodeValue ?? "").trim()}"`,
        snippet: el.outerHTML.slice(0, 160),
        element: el,
      });
    }
    current = walker.nextNode();
  }

  return hits;
};

const report = (hits: Hit[]) => {
  if (hits.length === 0) return;
  const isDev = Boolean(import.meta.env?.DEV) && !import.meta.env?.PROD;
  const header = `[debug-ui-assertion] Found ${hits.length} leaked debug element${hits.length === 1 ? "" : "s"}`;

  if (isDev) {
    // eslint-disable-next-line no-console
    console.group(`%c${header}`, "color:#fff;background:#dc2626;padding:2px 6px;border-radius:3px;font-weight:600;");
    hits.forEach((h, i) => {
      // eslint-disable-next-line no-console
      console.error(`#${i + 1} ${h.reason}`, h.element);
      h.element.setAttribute("data-debug-ui-leak", "true");
      (h.element as HTMLElement).style.outline = "3px solid #dc2626";
      (h.element as HTMLElement).style.outlineOffset = "2px";
    });
    // eslint-disable-next-line no-console
    console.groupEnd();
  } else {
    // eslint-disable-next-line no-console
    console.warn(header, hits.map((h) => h.reason));
  }
};

const DebugUiAssertion = () => {
  const location = useLocation();

  useEffect(() => {
    // Wait one frame so the new route has rendered, then scan.
    const id = window.setTimeout(() => {
      try {
        report(scanDom());
      } catch {
        /* never let the assertion break the app */
      }
    }, 50);
    return () => window.clearTimeout(id);
  }, [location.pathname]);

  return null;
};

export default DebugUiAssertion;
